import { useEffect, useRef, useState } from "react";
import { Button } from "@mui/material";
import { SuccessCheckmarkIcon } from "../../Icons/Icons";
import { useAppDispatch, useAppSelector } from "../../store";
import {
  getPredictionData,
  getPredictionSubmitState,
} from "../../store/selectors/PredictionSelectors";
import { getScrewProperties, getUploadedScrewData, getScrewFetchStatus } from "../../store/selectors/ScrewSelectors";
import {
  resetPredictionSlice,
  updatePredictionStep,
} from "../../store/reducers/PredictionReducers";
import { resetScrewUpload } from "../../store/reducers/ScrewsReducers";
import { onAddScrewsToDatasetAction } from "../../store/actions/PredictionActions";
import Loader from "../Loader";
import SystemMsg from "../SystemMsg";
import "./css/PredictionResults.scss";
import ScrewDropdownProps from "components/DropdownProps/ScrewDropdownProps";
import { getScrewsPropertiesAction } from "store/actions/ScrewsActions";

const ViewPredictionResultsScrews = () => {
  const dispatch = useAppDispatch();
  const predictionData = useAppSelector(getPredictionData);
  const screwData = useAppSelector(getUploadedScrewData);
  const predictionSubmitState = useAppSelector(getPredictionSubmitState);
  const screwProperties = useAppSelector(getScrewProperties);
  const screwFetchStatus = useAppSelector(getScrewFetchStatus);
  const [selectedPrediction, setSelectedPrediction] = useState<any>(null);
  const [progressStep, setProgressStep] = useState<number>(1);

  const getPropertyValue = (prediction: any, propertyName: string) => {
    const foundItem = prediction?.propertyvalues?.items?.find(
      (item: any) => item.propertyname === propertyName
    );
    return foundItem?.categoricalvalue;
  };
  const hasInitializedRef = useRef(false);
  const isScrewDataFetching = screwFetchStatus === "loading";

  useEffect(() => {    
    if (!hasInitializedRef.current && !screwProperties && !isScrewDataFetching) {
      hasInitializedRef.current = true;
      dispatch(getScrewsPropertiesAction());
    }
  }, [dispatch, isScrewDataFetching, screwProperties]);

  const viewPredictionRecord = (prediction: any, iterator: number) => {
    const { confidence, sample_id } = prediction;
    const ref = getPropertyValue(prediction, "ref");

    const activeClassName =
      selectedPrediction && getPropertyValue(selectedPrediction, "ref") === ref ? "selected" : "";

    const labelProperties = prediction?.propertyvalues?.items?.filter((item: any) => {
      const property = screwProperties?.items?.find((prop: any) => prop.propertyname === item.propertyname);
      return property?.islabel;
    });

    const nonLabelProperties = prediction?.propertyvalues?.items?.filter((item: any) => {
      const property = screwProperties?.items?.find((prop: any) => prop.propertyname === item.propertyname);
      return !property?.islabel;
    });

    // Sort non-label properties: brand and platform first, then rest alphabetically
    const sortedNonLabelProperties = nonLabelProperties?.sort((a: any, b: any) => {
      const aName = a.propertyname.toLowerCase();
      const bName = b.propertyname.toLowerCase();

      // If either is brand or platform, they come first
      if (aName === 'brand') return -1;
      if (bName === 'brand') return 1;
      if (aName === 'platform') return -1;
      if (bName === 'platform') return 1;

      // Otherwise sort alphabetically
      return aName.localeCompare(bName);
    });

    const sortedProperties = [...labelProperties, ...sortedNonLabelProperties];

    return (
      <div
        className={`prediction-item ${activeClassName}`}
        key={iterator}
        onClick={() => setSelectedPrediction(prediction)}
      >
        <h3>
          Prediction {iterator + 1}
          <span className="checkmark">
            <SuccessCheckmarkIcon />
          </span>
        </h3>
        <div className="record">
          <p>Confidence</p>
          <p className="confidence-value">{(confidence * 100).toFixed(1)}%</p>
        </div>
        {sortedProperties.map((item: any) => (
          <div className="record" key={item.propertyid}>
            <p>{item.propertyname}</p>
            <div className="loc-data">{item.categoricalvalue}</div>
          </div>
        ))}
      </div>
    );
  };

  const predictionMatches = predictionData?.map((prediction, iterator) =>
    viewPredictionRecord(prediction, iterator)
  );

  const onResetPrediction = () => {
    setSelectedPrediction(null);
    setProgressStep(1);
    dispatch(updatePredictionStep(1));
    dispatch(resetScrewUpload());
    dispatch(resetPredictionSlice());
  };

  const onContinue = () => {
    // TODO: Metadata selection step is temporarily disabled because the endpoint does not support it.
    // To re-enable:
    // 1. Remove this comment block
    // 2. Uncomment the original code below
    // 3. Remove the direct submission code
    
    // Original code:
    // progressStep >= 1 && progressStep < 3
    //   ? setProgressStep((prev) => prev + 1)
    //   : dispatch(onAddScrewsToDatasetAction(selectedPrediction));

    // For now, directly submit after step 2
    if (progressStep === 1) {
      setProgressStep(2);
    } else {
      console.log("Dispatching onAddScrewsToDatasetAction with:", selectedPrediction);
      dispatch(onAddScrewsToDatasetAction(selectedPrediction));
    }
  };

  const onGoBack = () =>
    progressStep === 1
      ? onResetPrediction()
      : setProgressStep((prev) => prev - 1);

  // const continueLabel = progressStep < 3 ? "Continue" : "Add to Dataset";
  const continueLabel = progressStep === 1 ? "Continue" : "Add to Dataset"; // Modified to reflect 2-step flow
  const backlabel = progressStep === 1 ? "Cancel" : "Edit";

  const predictionControls = (
    <>
      <div className="row">
        <Button
          variant="contained"
          className="btn btn-blue"
          disabled={!selectedPrediction?.sample_id}
          onClick={onContinue}
        >
          {continueLabel}
        </Button>
      </div>
      <div className="row">
        <Button
          variant="outlined"
          className="btn btn-outlined"
          onClick={onGoBack}
        >
          {backlabel}
        </Button>
      </div>
    </>
  );

  const selectedPredictionDetails = (
    <div className="row prediction-results">
      <h2>Prediction results</h2>
      <br/>
      <div className="row">
        <span>reference</span>
        <p>{getPropertyValue(selectedPrediction, "ref")}</p>
      </div>
      <div className="row">
        <span>Confidence</span>
        <p>{(selectedPrediction?.confidence * 100).toFixed(1)}%</p>
      </div>

    
      {selectedPrediction?.propertyvalues?.items?.some((item: any) => item.propertyname !== "ref") && (
        <>
          <div className="horizontal-divider"></div>
          <h2>Metadata</h2>
          <br/>
        </>
      )}

      {/* First show brand and platform */}
      {selectedPrediction?.propertyvalues?.items?.filter((item: any) => 
        item.propertyname === "brand" || item.propertyname === "platform"
      ).sort((a: any, b: any) => {
        const aName = a.propertyname.toLowerCase();
        const bName = b.propertyname.toLowerCase();
        // Brand comes before platform
        if (aName === 'brand') return -1;
        if (bName === 'brand') return 1;
        return aName.localeCompare(bName);
      }).map((item: any) => (
        <div className="row" key={item.propertyid}>
          <span>{item.propertyname}</span>
          <p>{item.categoricalvalue}</p>
        </div>
      ))}

      {/* Then show other properties alphabetically */}
      {selectedPrediction?.propertyvalues?.items?.filter((item: any) => 
        item.propertyname !== "ref" && 
        item.propertyname !== "brand" && 
        item.propertyname !== "platform"
      ).sort((a: any, b: any) => 
        a.propertyname.toLowerCase().localeCompare(b.propertyname.toLowerCase())
      ).map((item: any) => (
        <div className="row" key={item.propertyid}>
          <span>{item.propertyname}</span>
          <p>{item.categoricalvalue}</p>
        </div>
      ))}


    </div>
  );

  const properties = screwProperties?.items || [];
  const nonLabelProperties = properties.filter((prop: any) => !prop.islabel).sort((a: any, b: any) => a.propertyname.localeCompare(b.propertyname));

  const screwOptions = (
    <div className="row">
      <h2>Data labels</h2>
      {nonLabelProperties.map((prop: any) => {

        // Find the existing property value in the prediction
        const existingProperty = selectedPrediction?.propertyvalues?.items?.find(
          (item: any) => item.propertyname.toLowerCase() === prop.propertyname.toLowerCase()
        );

        return (
          <ScrewDropdownProps
            key={prop.propertyid}
            onChooseScrewProperty={(evt: any) => {
              const currentItems = selectedPrediction?.propertyvalues?.items || [];
              
              // Always maintain existing properties and add/update the changed one
              const updatedItems = [...currentItems];
              const existingIndex = updatedItems.findIndex(
                (item: any) => item.propertyname.toLowerCase() === prop.propertyname.toLowerCase()
              );

              const newPropertyValue = {
                propertyname: prop.propertyname,
                propertyid: prop.propertyid,
                categoricalvalue: evt.target.value,
                description: null,
                islabel: false,
                numericalvalue: null
              };

              if (existingIndex >= 0) {
                updatedItems[existingIndex] = newPropertyValue;
              } else {
                // Add new property
                updatedItems.push(newPropertyValue);
              }
              
              setSelectedPrediction({
                ...selectedPrediction,
                propertyvalues: {
                  ...selectedPrediction?.propertyvalues,
                  items: updatedItems
                }
              });
            }}
            selectedScrewProperty={existingProperty?.categoricalvalue || ""}
            propertyType={prop.propertyname}
          />
        );
      })}
    </div>
  );

  const rightSideContent =
    progressStep === 2
      ? selectedPredictionDetails
      // TODO: Metadata selection step is temporarily disabled
      // To restore, uncomment this:
      // : progressStep === 3
      // ? screwOptions
      : predictionMatches;

  const predictionSubmitSuccess = (
    <div className="prediction-msg">
      <SystemMsg
        message="Successfully added to dataset!"
        messageType="success"
      />
      <div className="buttons-holder">
        <Button className="btn btn-blue" onClick={onResetPrediction}>
          Upload another screw
        </Button>
      </div>
    </div>
  );

  const predictionsViewer = (
    <div className="prediction-results-wrapper">
      <div className="left-side">
        {screwData?.[0]?.imagepath && (
          <div className="screw-img-holder">
            <img src={screwData[0].imagepath} alt={screwData[0].sampleid} />
          </div>
        )}
        {predictionControls}
      </div>
      <div
        className="right-side"
        style={progressStep < 3 ? { display: "flex" } : {}}
      >
        {rightSideContent}
      </div>
    </div>
  );

  const predictionViewOptions =
    predictionSubmitState === "success"
      ? predictionSubmitSuccess
      : predictionsViewer;

  return predictionSubmitState === "loading" ? (
    <Loader />
  ) : predictionSubmitState === "error" ? (
    <div className="prediction-msg">
      <SystemMsg />
      <Button onClick={onResetPrediction}>Retry?</Button>
    </div>
  ) : (
    predictionViewOptions
  );
};

export default ViewPredictionResultsScrews; 